// Shared text helpers for draw UI

type Translator = (key: string, values?: Record<string, string | number>) => string;

/**
 * Translate stage and round display names to localized text.
 *
 * @param stageName - The canonical stage name (e.g., "Qualifying Pool 1", "Main Draw", "Plate")
 * @param t - Translation function from useTranslations('AdminDraw.drawForm.drawPreview')
 *
 * @example
 * ```tsx
 * const tDrawPreview = useTranslations('AdminDraw.drawForm.drawPreview');
 * const translatedName = translateStageName("Qualifying Pool 1", tDrawPreview);
 * // Returns: "資格賽池 1" (Chinese) or "予選プール 1" (Japanese)
 * ```
 *
 * @important The translation function MUST use the 'AdminDraw.drawForm.drawPreview' namespace
 * to access the required translation keys like 'qualifyingPool', 'mainDraw', 'plate', etc.
 */
export function translateStageName(stageName: string, t: Translator): string {
  // Qualifying Pool N - expects t('qualifyingPool') from AdminDraw.drawForm.drawPreview
  const qualifyingPoolMatch = stageName.match(/^Qualifying Pool (\d+)$/);
  if (qualifyingPoolMatch) return `${t('qualifyingPool')} ${qualifyingPoolMatch[1]}`;

  // Core stage labels - all expect keys from AdminDraw.drawForm.drawPreview namespace
  if (stageName === 'Main Draw') return t('mainDraw');
  if (stageName === 'Plate') return t('plate');
  if (stageName === 'Consolation Plate') return t('consolationPlate');
  if (stageName === 'Special Plate') return t('specialPlate');
  if (stageName === 'Custom Plate') return t('customPlate');

  // Custom Plate N (e.g., "Consolation 1", "Consolation 2")
  const consolationMatch = stageName.match(/^Consolation (\d+)$/);
  if (consolationMatch) return `${t('consolation')} ${consolationMatch[1]}`;

  if (stageName === '3rd/4th Playoff') return t('thirdFourthPlayoff');

  // Position playoff brackets (e.g., "5/6 Playoff", "7/8 Playoff", "9/10 Playoff")
  // Only translate "Playoff", keep the position numbers
  const positionPlayoffMatch = stageName.match(/^(\d+\/\d+)\s+Playoff$/);
  if (positionPlayoffMatch) {
    return `${positionPlayoffMatch[1]} ${t('playoff')}`;
  }

  // Pool Play-offs orphan (odd team counts): "9th Place", "11th Place", …
  const lastPlaceMatch = stageName.match(/^(\d+)(?:st|nd|rd|th) Place$/);
  if (lastPlaceMatch) return t('lastPlace', { number: Number(lastPlaceMatch[1]) });

  // Round name replacements when baked into stage name (e.g., "Main Draw Quarter Finals")
  // All expect keys from AdminDraw.drawForm.drawPreview namespace
  let translatedName = stageName;
  // Generic "Round of N" - expects t('roundOf', { number: N })
  translatedName = translatedName.replace(/Round of (\d+)/g, (_m, n) =>
    t('roundOf', { number: Number(n) }),
  );
  if (translatedName.includes('Quarter Finals')) {
    translatedName = translatedName.replace('Quarter Finals', t('quarterFinals'));
  }
  if (translatedName.includes('Semi Finals')) {
    translatedName = translatedName.replace('Semi Finals', t('semiFinals'));
  }
  if (translatedName.includes('Final')) {
    translatedName = translatedName.replace('Final', t('final'));
  }

  // Remove duplicated stage prefixes when present
  if (translatedName.includes('Main Draw ')) {
    translatedName = translatedName.replace(/Main Draw /g, '');
  }
  if (translatedName.includes('Qualifying Pool ')) {
    translatedName = translatedName.replace(/Qualifying Pool \d+ /g, '');
  }
  if (translatedName.includes('Plate ')) {
    translatedName = translatedName.replace(/Plate /g, '');
  }
  if (translatedName.includes('Consolation Plate ')) {
    translatedName = translatedName.replace(/Consolation Plate /g, '');
  }
  if (translatedName.includes('Special Plate ')) {
    translatedName = translatedName.replace(/Special Plate /g, '');
  }
  if (translatedName.includes('Custom Plate ')) {
    translatedName = translatedName.replace(/Custom Plate /g, '');
  }
  // Strip "Consolation N " prefix from round names
  translatedName = translatedName.replace(/Consolation \d+ /g, '');
  if (translatedName.includes('3rd/4th Playoff ')) {
    translatedName = translatedName.replace(/3rd\/4th Playoff /g, '');
  }
  // Replace position playoff patterns (e.g., "5/6 Playoff", "7/8 Playoff")
  translatedName = translatedName.replace(/(\d+\/\d+)\s+Playoff/g, (_m, positions) => {
    return `${positions} ${t('playoff')}`;
  });

  return translatedName;
}

// Players still alive in an elimination round, read off its canonical name
function playersInRound(roundName: string): number | null {
  if (roundName === 'Final') return 2;
  if (roundName === 'Semi Finals') return 4;
  if (roundName === 'Quarter Finals') return 8;
  const roundOfMatch = roundName.match(/^Round of (\d+)$/);
  return roundOfMatch ? Number(roundOfMatch[1]) : null;
}

/**
 * Stage label for one round of a Monrad position playoff. The stored stage name carries only
 * the top two places ("33/34 Playoff"), which reads the same for every round; this shows the
 * places still in play in that round instead ("33rd–48th Playoff" for its Round of 16).
 *
 * Anything that is not a position playoff, or whose round size is unknown, falls back to
 * `translateStageName`. Expects t('positionPlayoff', { start, end }) from the same namespace.
 */
export function translatePlayoffStageName(
  stageName: string,
  roundName: string | null | undefined,
  t: Translator,
): string {
  const positionPlayoffMatch = stageName.match(/^(\d+)\/\d+\s+Playoff$/);
  const players = roundName ? playersInRound(roundName) : null;
  if (!positionPlayoffMatch || !players) return translateStageName(stageName, t);

  const start = Number(positionPlayoffMatch[1]);
  return t('positionPlayoff', { start, end: start + players - 1 });
}
